import type { UploadHealthSnapshot, UploadTask } from './uploads';

export const UPLOAD_SHARED_WORKER_PROTOCOL_VERSION = 2 as const;

export interface SharedUploadProgress {
  /** Distinguishes local file reads from bytes sent to GCS so the UI never reuses one stage's bar. */
  stage: 'verification' | 'transfer';
  bytes: number;
  totalBytes: number;
  speedBps?: number;
}

export interface SharedUploadSnapshot {
  revision: number;
  items: UploadTask[];
  progressByUploadTaskId: Record<string, SharedUploadProgress>;
  leaderTabId: string | null;
  healthBySessionId: Record<string, UploadHealthSnapshot>;
}

export interface UploadHealthCandidate {
  sessionId: string;
  reportedBytes: number;
}

export type UploadOffsetProbeResult =
  | { sessionId: string; status: 'available'; offset: number }
  | { sessionId: string; status: 'expired' }
  | { sessionId: string; status: 'inconclusive' };

interface ClientMessageBase {
  version: typeof UPLOAD_SHARED_WORKER_PROTOCOL_VERSION;
  uid: string;
  tabId: string;
}

export type UploadWorkerClientMessage =
  | (ClientMessageBase & { type: 'connect' })
  | (ClientMessageBase & { type: 'heartbeat' })
  | (ClientMessageBase & { type: 'disconnect' })
  | (ClientMessageBase & { type: 'set-leader'; leader: boolean })
  | (ClientMessageBase & { type: 'publish-snapshot'; items: UploadTask[] })
  | (ClientMessageBase & {
      type: 'publish-progress';
      progressByUploadTaskId: Record<string, SharedUploadProgress>;
    })
  | (ClientMessageBase & { type: 'retain-file'; uploadTaskId: string; file: File })
  | (ClientMessageBase & { type: 'release-file'; uploadTaskId: string })
  | (ClientMessageBase & { type: 'request-snapshot'; requestId: string })
  | (ClientMessageBase & { type: 'request-files'; requestId: string; uploadTaskIds: string[] })
  | (ClientMessageBase & { type: 'track-health'; candidates: UploadHealthCandidate[] })
  | (ClientMessageBase & {
      type: 'probe-results';
      requestId: string;
      results: UploadOffsetProbeResult[];
    });

interface WorkerMessageBase {
  version: typeof UPLOAD_SHARED_WORKER_PROTOCOL_VERSION;
  uid: string;
}

export type UploadWorkerServerMessage =
  | (WorkerMessageBase & { type: 'ready'; snapshot: SharedUploadSnapshot })
  | (WorkerMessageBase & { type: 'snapshot'; snapshot: SharedUploadSnapshot })
  | (WorkerMessageBase & {
      type: 'progress';
      progressByUploadTaskId: Record<string, SharedUploadProgress>;
    })
  | (WorkerMessageBase & {
      type: 'snapshot-response';
      requestId: string;
      snapshot: SharedUploadSnapshot;
    })
  | (WorkerMessageBase & {
      type: 'files-response';
      requestId: string;
      files: Array<{ uploadTaskId: string; file: File }>;
    })
  | (WorkerMessageBase & { type: 'heartbeat-ack' })
  | (WorkerMessageBase & {
      type: 'health';
      healthBySessionId: Record<string, UploadHealthSnapshot>;
    })
  | (WorkerMessageBase & { type: 'probe-offsets'; requestId: string; sessionIds: string[] })
  | (WorkerMessageBase & { type: 'error'; requestId?: string; code: string });

export function isUploadWorkerServerMessage(value: unknown): value is UploadWorkerServerMessage {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as { version?: unknown; uid?: unknown; type?: unknown };
  return (
    candidate.version === UPLOAD_SHARED_WORKER_PROTOCOL_VERSION &&
    typeof candidate.uid === 'string' &&
    typeof candidate.type === 'string'
  );
}
